/* @jsx h */
import { defineComponent, h, computed } from "vue";
import {
  useLocalization,
  ResponsiveTable,
  type ResponsiveTableColumn,
} from "@lemmonhq/uikit";
import { formatValue, type CellFormat } from "../format";
import { NestedReportTable } from "./NestedReportTable";

type ColumnSpec = {
  key: string;
  labelKey: string;
  format: CellFormat;
  align?: "left" | "right";
  total?: boolean;
  muted?: boolean;
};

/** Sentinel appended to the row list so the totals footer renders through `renderFullWidthRow`. */
const TOTALS_ROW_KEY = "__dataroom_totals__";

export const ReportTable = defineComponent({
  name: "DataRoomReportTable",
  props: {
    spec: { type: Object, required: true },
    rows: { type: Array, default: () => [] },
    currency: { type: String, default: "CHF" },
    loading: { type: Boolean, default: false },
    loadingMore: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    onLoadMore: { type: Function, default: undefined },
    parameters: { type: Object, default: () => ({}) },
    onParameterChange: { type: Function, default: undefined },
  },
  setup(props: any) {
    const { t } = useLocalization();

    const columns = computed<ColumnSpec[]>(() => props.spec?.columns ?? []);
    const rows = computed<Array<Record<string, unknown>>>(
      () => props.rows ?? [],
    );

    const cell = (row: Record<string, unknown>, c: ColumnSpec) => {
      const value = row[c.key];
      const numeric =
        (c.format === "money" ||
          c.format === "integer" ||
          c.format === "quantity") &&
        typeof value === "number";
      return (
        <span
          class={[
            numeric ? "tabular-nums" : "",
            numeric && (value as number) < 0 ? "text-error" : "",
            c.muted ? "text-base-content/55" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {formatValue(value, c.format, props.currency)}
        </span>
      );
    };

    const totalColumns = computed(() => columns.value.filter((c) => c.total));
    const showTotals = computed(
      () => !!props.spec?.showTotals && totalColumns.value.length > 0,
    );
    const totals = computed<Record<string, number>>(() => {
      const acc: Record<string, number> = {};
      if (!showTotals.value) return acc;
      for (const c of totalColumns.value) {
        acc[c.key] = rows.value.reduce(
          (sum, row) => sum + (Number(row[c.key]) || 0),
          0,
        );
      }
      return acc;
    });

    const tableColumns = computed<ResponsiveTableColumn[]>(() =>
      columns.value.map((c, i) => ({
        key: c.key,
        label: t(c.labelKey),
        headerClass: c.align === "right" ? "text-right" : "",
        cellClass: c.align === "right" ? "text-right" : "",
        mobilePriority: i === 0 ? "primary" : "secondary",
        render: (row: Record<string, unknown>) => cell(row, c),
      })),
    );

    const displayRows = computed(() =>
      showTotals.value
        ? [...rows.value, { [TOTALS_ROW_KEY]: true }]
        : rows.value,
    );

    const renderTotalsRow = () => (
      <div class="flex flex-wrap items-center gap-4 border-t-2 bg-base-200/60 px-4 py-2.5 text-sm font-semibold">
        <span class="text-[11px] uppercase tracking-wider text-base-content/60">
          {t("reports.dataroom.table_total_loaded").replace(
            "{count}",
            String(rows.value.length),
          )}
        </span>
        {totalColumns.value.map((c) => (
          <span
            key={c.key}
            class={`ml-auto tabular-nums ${totals.value[c.key] < 0 ? "text-error" : "text-accent"}`}
          >
            {t(c.labelKey)}: {formatValue(totals.value[c.key], c.format, props.currency)}
          </span>
        ))}
      </div>
    );

    return () => {
      if (props.spec?.hierarchy) {
        return (
          <NestedReportTable
            spec={props.spec}
            rows={rows.value}
            parameters={props.parameters}
            currency={props.currency}
            loading={props.loading}
            loadingMore={props.loadingMore}
            hasMore={props.hasMore}
            onLoadMore={props.onLoadMore}
            onParameterChange={props.onParameterChange}
          />
        );
      }

      return (
        <div class="rounded-2xl bg-base-100 overflow-hidden shadow-sm">
          <ResponsiveTable
            columns={tableColumns.value}
            rows={displayRows.value}
            rowKey={(row: Record<string, unknown>) =>
              row[TOTALS_ROW_KEY] ? TOTALS_ROW_KEY : JSON.stringify(row)
            }
            loading={props.loading}
            emptyMessage={t("reports.dataroom.table_empty")}
            renderFullWidthRow={(row: Record<string, unknown>) =>
              row[TOTALS_ROW_KEY] ? renderTotalsRow() : null
            }
          />
          {props.spec?.grainKey && (
            <div class="border-t bg-base-200/40 px-4 py-2 text-[11px] text-base-content/65">
              {t(props.spec.grainKey)}
            </div>
          )}
          {props.hasMore && (
            <div class="flex justify-center border-t bg-base-200/40 p-3">
              {/*
                Was btn-ghost: no border, no background, so the primary way to
                see more rows read as inert text. It now looks like a control.
              */}
              <button
                type="button"
                class="btn btn-sm bg-base-100 text-base-content hover:border-primary hover:bg-primary/10 hover:text-primary"
                disabled={props.loadingMore}
                onClick={() => props.onLoadMore?.()}
              >
                {props.loadingMore ? (
                  <span class="loading loading-spinner loading-xs" />
                ) : (
                  <span class="material-symbols-outlined text-base">
                    expand_more
                  </span>
                )}
                {t("reports.dataroom.table_load_more")}
              </button>
            </div>
          )}
        </div>
      );
    };
  },
});
